import { type ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useCountryName } from '../i18n/country-name';
import { AUTO_REFRESH_MS, Badge, Button, CheckRow, EmptyState, Field, FormRow, ListTable, Notice, PageHeader, Panel, SegmentedControl, TextInput, type Column, Text, InlineStack, DateRangeInput, Row, Stack, ChoiceGroup, useAutoRefresh } from '@vello/web-theme';
import { type AccessPlatform, type AccessSessionView, type AccessSessionsView, type ApiError } from '@vello/api-client';
import {
  ACCESS_MAX_DAYS, ACCESS_PAGE, BI_EXPORT_POLICY, INITIAL_ACCESS_FILTERS, LIST_PRESETS, accessFileName, accessQuery, biErrorView, customPeriodProblem, editDays, grantsAllow, periodOf, shownDays, toggled, userFilterProblem,
  type AccessFilters, type ListPreset,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { PersonName } from '../components/person-name';
import { downloadText } from '../components/bi/download';
import { BiProblem, useBiText } from '../components/bi/bi-ui';
const PLATFORMS: readonly AccessPlatform[] = ['ios', 'android', 'web'];
const today = (): string => new Date().toISOString().slice(0, 10);

export function AccessReportScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { toast } = useConsole();
  const text = useBiText();
  const { t, v, lang } = text;
  const countryName = useCountryName();
  const fromId = useId();
  const userId = useId();
  const exportWord = grants !== undefined && grantsAllow(grants, BI_EXPORT_POLICY);

  const [filters, setFilters] = useState<AccessFilters>(INITIAL_ACCESS_FILTERS);
  const [pages, setPages] = useState<readonly AccessSessionsView[]>([]);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [more, setMore] = useState(false);
  const quiet = useRef(false);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => accessQuery(filters, today()), [filters]);
  const shown = shownDays(filters.preset, filters, today());
  const periodProblem = filters.preset === 'custom' ? customPeriodProblem(filters.from, filters.to, ACCESS_MAX_DAYS) : undefined;
  const userProblem = userFilterProblem(filters.user);

  useEffect(() => {
    if (query === undefined) {
      setPages([]);
      return undefined;
    }
    let live = true;
    // A background tick keeps the rows on screen (no loading block) and, if it fails, keeps them too.
    const background = quiet.current;
    quiet.current = false;
    if (!background) {
      setLoading(true);
      setError(undefined);
    }
    void (async () => {
      const res = await runtime.accessReport.sessions({ ...query, limit: ACCESS_PAGE });
      if (!live) return;
      setLoading(false);
      if (res.ok) {
        setPages([res.value]);
        setError(undefined);
      } else if (!background) {
        setPages([]);
        setError(res.error);
      }
    })();
    return () => {
      live = false;
    };
  }, [runtime, query, tick]);

  const refresh = useCallback((): void => setTick((n) => n + 1), []);
  // Re-read by itself (no refresh button) while the first page is what is on screen; once older pages are loaded it would drop them.
  useAutoRefresh(() => {
    quiet.current = true;
    setTick((n) => n + 1);
  }, { intervalMs: AUTO_REFRESH_MS.report, enabled: query !== undefined && pages.length <= 1 });
  const change = (patch: Partial<AccessFilters>): void => setFilters((f) => ({ ...f, ...patch }));

  const last = pages[pages.length - 1];
  const rows = pages.flatMap((p) => p.rows);
  const loadMore = async (): Promise<void> => {
    if (query === undefined || last?.nextCursor === undefined) return;
    setMore(true);
    const res = await runtime.accessReport.sessions({ ...query, limit: ACCESS_PAGE, cursor: last.nextCursor });
    setMore(false);
    if (res.ok) setPages((p) => [...p, res.value]);
    else setError(res.error);
  };

  const period = periodOf(filters.preset, filters, today(), ACCESS_MAX_DAYS);
  const exportCsv = async (): Promise<void> => {
    if (query === undefined || period === undefined) return;
    setExporting(true);
    const res = await runtime.accessReport.exportCsv(query);
    setExporting(false);
    if (!res.ok) {
      toast({ tone: 'warning', message: t(biErrorView(res.error).messageId) });
      return;
    }
    const name = accessFileName(period);
    toast(downloadText(name, res.value) ? { tone: 'success', message: t('ac.csv.done', { name }) } : { tone: 'warning', message: t('ac.csv.failed') });
  };

  const instant = (iso: string): string => new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(iso));
  const statusOf = (r: AccessSessionView): { readonly label: string; readonly tone: 'neutral' | 'success' | 'warning' } =>
    r.status === 'revoked' ? { label: t('ac.status.revoked'), tone: 'neutral' } : r.expired ? { label: t('ac.status.expired'), tone: 'warning' } : { label: t('ac.status.active'), tone: 'success' };
  const reasonOf = (r: AccessSessionView): string | undefined => (r.revocationReason === null ? undefined : v('access.session_revocation_reason', r.revocationReason));

  const columns: readonly Column<AccessSessionView>[] = [
    { key: 'when', header: t('ac.col.when'), width: '1fr', render: (r) => <span className="tnum">{instant(r.issuedAt)}</span> },
    { key: 'last', header: t('ac.col.last'), width: '1fr', render: (r) => <span className="tnum">{instant(r.lastUsedAt)}</span> },
    { key: 'account', header: t('ac.col.account'), width: '1.5fr', render: (r) => <PersonName id={r.userId} /> },
    { key: 'country', header: t('ac.col.country'), width: 'max-content', render: (r) => countryName(r.country) },
    { key: 'who', header: t('ac.col.who'), width: '0.9fr', render: (r) => v('access.group', r.group) },
    { key: 'realm', header: t('ac.col.realm'), width: '1.1fr', render: (r) => v('identity.realm', r.realm) },
    { key: 'device', header: t('ac.col.device'), width: '0.8fr', render: (r) => v('common.client_platform', r.platform) },
    { key: 'version', header: t('ac.col.version'), width: '0.8fr', render: (r) => (r.appVersion === null ? <Text role="caption" tone="secondary">—</Text> : <span className="tnum">{r.appVersion}</span>) },
    { key: 'status', header: t('ac.col.status'), width: '1.2fr', render: (r) => { const s = statusOf(r); const why = reasonOf(r); return <InlineStack gap={1} align="start"><Badge tone={s.tone}>{s.label}</Badge>{r.revokedAt !== null && <Text role="caption" tone="secondary">{instant(r.revokedAt)}{why !== undefined ? ` · ${why}` : ''}</Text>}</InlineStack>; } },
  ];

  const scope = last?.scope;
  return (
    <div>
      <PageHeader
        title={t('ac.title')}
        subtitle={t('ac.subtitle')}
        actions={
          <>
            {exportWord && <Button variant="primary" icon="download" disabled={exporting || query === undefined || rows.length === 0} loading={exporting} onClick={() => void exportCsv()}>{t('ac.csv')}</Button>}
          </>
        }
      />
      <Stack gap={5}>
        {scope !== undefined && <Notice tone="neutral">{'global' in scope ? t('ac.scope.global') : t('ac.scope.countries', { list: scope.countries.map((c) => countryName(c)).join(', ') })}</Notice>}
        <Panel>
          <Stack gap={4}>
            <SegmentedControl<AccessFilters['group']>
              ariaLabel={t('ac.group')}
              value={filters.group}
              onChange={(group) => change({ group })}
              options={[{ id: 'all', label: v('access.group', 'all') }, { id: 'operators', label: v('access.group', 'operators') }, { id: 'clients', label: v('access.group', 'clients') }]}
            />
            <Row gap={4} align="center" wrap>
              <SegmentedControl<string>
                ariaLabel={t('ol.period')}
                value={String(filters.preset)}
                onChange={(id) => change({ preset: Number(id) as ListPreset })}
                options={LIST_PRESETS.filter((p) => p <= ACCESS_MAX_DAYS).map((p) => ({ id: String(p), label: t(`ol.preset.${p}`) }))}
              />
              <DateRangeInput idPrefix={fromId} from={shown.from} to={shown.to} onFromChange={(d) => change(editDays(filters.preset, filters, today(), 'from', d))} onToChange={(d) => change(editDays(filters.preset, filters, today(), 'to', d))} fromLabel={t('ol.from')} toLabel={t('ol.to')} invalid={periodProblem !== undefined} />
            </Row>
            {periodProblem !== undefined && <Notice tone="warning">{t(`ol.problem.${periodProblem}`, { max: ACCESS_MAX_DAYS })}</Notice>}
            <ChoiceGroup>
              <Text as="legend" role="caption" tone="secondary">{t('ac.platform')}</Text>
              {PLATFORMS.map((p) => (
                <CheckRow key={p} id={`${fromId}-p-${p}`} checked={filters.platforms.includes(p)} onChange={(on) => change({ platforms: PLATFORMS.filter((x) => toggled(filters.platforms, p, on).includes(x)) })}>{v('common.client_platform', p)}</CheckRow>
              ))}
            </ChoiceGroup>
            <FormRow columns="minmax(0, 26rem) minmax(0, 14rem)">
              <Field label={t('ac.user')} htmlFor={userId} {...(userProblem ? { error: t('ac.user.invalid') } : {})}>
                <TextInput id={userId} value={filters.user} spellCheck={false} onChange={(e) => change({ user: e.target.value })} />
              </Field>
              <Field label={t('ac.status')} htmlFor={`${userId}-status`}>
                <SegmentedControl<AccessFilters['status']>
                  ariaLabel={t('ac.status')}
                  value={filters.status}
                  onChange={(status) => change({ status })}
                  options={[{ id: '', label: t('ac.status.any') }, { id: 'active', label: t('ac.status.active') }, { id: 'revoked', label: t('ac.status.revoked') }]}
                />
              </Field>
            </FormRow>
          </Stack>
        </Panel>
        {error !== undefined && <BiProblem error={biErrorView(error)} onRetry={refresh} text={text} />}
        {error === undefined && (
          <ListTable<AccessSessionView>
            columns={columns}
            rows={rows}
            rowKey={(r) => r.sessionId}
            loading={loading}
            loadingLabel={t('ui.loading')}
            minWidth={980}
            countLabel={t('ac.loaded', { count: text.fmt.count(rows.length) })}
            empty={<EmptyState title={t('ac.empty.title')} detail={t('ol.empty.detail')} />}
          />
        )}
        {last?.nextCursor !== undefined && (
          <div><Button variant="secondary" loading={more} disabled={more} onClick={() => void loadMore()}>{t('ac.more')}</Button></div>
        )}
        <Text role="caption" tone="secondary">{t('ac.logged')}</Text>
      </Stack>
    </div>
  );
}
